import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Input, List, Tag, Typography, Empty, Spin, Space } from 'antd';
import {
  SearchOutlined, FileTextOutlined,
  BugOutlined, CheckSquareOutlined, BookOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import searchService from '../../services/searchService';
import useUiStore from '../../store/uiStore';
import './GlobalSearchModal.css';

const { Text } = Typography;

const typeIcons = {
  epic: <ThunderboltOutlined style={{ color: '#722ed1' }} />,
  story: <BookOutlined style={{ color: '#52c41a' }} />,
  task: <CheckSquareOutlined style={{ color: '#1890ff' }} />,
  bug: <BugOutlined style={{ color: '#f5222d' }} />,
};

const GlobalSearchModal = () => {
  const navigate = useNavigate();
  const { searchModalOpen, toggleSearchModal } = useUiStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ issues: [], pages: [] });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchModalOpen) {
      setQuery('');
      setResults({ issues: [], pages: [] });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [searchModalOpen]);

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        toggleSearchModal();
      }
      if (e.key === 'Escape' && searchModalOpen) {
        toggleSearchModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchModalOpen, toggleSearchModal]);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults({ issues: [], pages: [] });
      return;
    }
    setLoading(true);
    try {
      const res = await searchService.search(q);
      setResults(res.data || { issues: [], pages: [] });
    } catch {
      setResults({ issues: [], pages: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelectIssue = (issue) => {
    toggleSearchModal();
    navigate(`/projects/${issue.project_id}/issues/${issue.issue_key}`);
  };

  const handleSelectPage = (page) => {
    toggleSearchModal();
    navigate(`/spaces/${page.space_id}/pages/${page.id}`);
  };

  const hasResults = results.issues.length > 0 || results.pages.length > 0;
  const statusColors = { todo: 'default', in_progress: 'processing', done: 'success' };

  return (
    <Modal
      open={searchModalOpen}
      onCancel={toggleSearchModal}
      footer={null}
      closable={false}
      width={600}
      className="global-search-modal"
      style={{ top: 80 }}
    >
      <Input
        ref={inputRef}
        prefix={<SearchOutlined />}
        placeholder="Search tickets and pages..."
        value={query}
        onChange={handleChange}
        size="large"
        style={{ borderRadius: 8 }}
        suffix={<Text type="secondary" style={{ fontSize: 12 }}>ESC to close</Text>}
      />

      <div className="search-results">
        {loading && <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>}

        {!loading && query.length >= 2 && !hasResults && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No results found" />
        )}

        {results.issues.length > 0 && (
          <div className="search-section">
            <Text type="secondary" className="search-section-title">Tickets</Text>
            <List
              size="small"
              dataSource={results.issues}
              renderItem={(item) => (
                <List.Item className="search-result-item" onClick={() => handleSelectIssue(item)}>
                  <Space>
                    {typeIcons[item.issue_type_name?.toLowerCase()] || <CheckSquareOutlined />}
                    <Text strong style={{ fontSize: 12 }}>{item.issue_key}</Text>
                    <Text>{item.title}</Text>
                  </Space>
                  <Tag color={statusColors[item.status_category]}>{item.status_name}</Tag>
                </List.Item>
              )}
            />
          </div>
        )}

        {results.pages.length > 0 && (
          <div className="search-section">
            <Text type="secondary" className="search-section-title">Pages</Text>
            <List
              size="small"
              dataSource={results.pages}
              renderItem={(item) => (
                <List.Item className="search-result-item" onClick={() => handleSelectPage(item)}>
                  <Space>
                    <FileTextOutlined />
                    <Text>{item.title}</Text>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>{item.space_name}</Text>
                </List.Item>
              )}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GlobalSearchModal;
